import type { UrlMatchResult, UrlSegmentGroup } from '@angular/router';
import { convertToParamMap, UrlSegment } from '@angular/router';
import { isInteger } from '@freelancer/utils';

/**
 * The purpose of this route matcher is to attempt to mirror the nginx rules
 * for /jobs to /projects including:
 * - rewrite ^/jobs/project-(\d+)/$ https://$host/projects/project-$1/
 *   permanent;
 * - rewrite ^/jobs/([a-zA-Z\-]+([\.|\-]\d+)?)\/$ https://$host/projects/$1/
 *   permanent;
 * - rewrite ^/jobs/([a-zA-Z0-9\.\-]+)/([a-zA-Z\-]+([\.|\-]\d+)?)\/$
 *   https://$host/projects/$1/$2/ permanent;
 *
 * @param segments segments of current route url divided by `/`
 * @param group not used
 * @param route current route
 */
export function jobsRouteMatcher(
  segments: readonly UrlSegment[],
  group: UrlSegmentGroup,
): UrlMatchResult | null {
  const url = `/${group.toString()}`;

  const urlPaths = segments.map(urlSegment => {
    if (urlSegment.path === 'jobs') {
      return 'projects';
    }
    return urlSegment.path;
  });

  // Case #1: Dealing with project-[id].
  // e.g. /jobs/project-123456
  if (
    /^\/jobs\/project-(\d+)/.test(url) &&
    // Check if the urlPaths[1] exists.
    urlPaths[1] &&
    // Check if urlPaths starts with "project-".
    urlPaths[1].startsWith('project-') &&
    // In the case that it does, split out the number.
    isInteger(urlPaths[1].split('-')[1])
  ) {
    return {
      consumed: [new UrlSegment('projects', {})],
      posParams: {
        id: {
          path: urlPaths[1].split('-')[1],
          parameters: {},
          parameterMap: convertToParamMap({}),
        },
      },
    };
  }

  // Case #2: Handle SEO links.
  // e.g. /seo1/seo2, /seo1/seo2/tab
  if (/^\/jobs\/([a-zA-Z0-9.-]+)\/([a-zA-Z-]+([.|-]\d+)?)/.test(url)) {
    return {
      consumed: [new UrlSegment('projects', {})],
      posParams: {
        seoUrl: {
          path: urlPaths.join('/'),
          parameters: {},
          parameterMap: convertToParamMap({}),
        },
      },
    };
  }

  return null;
}
